/**
 * useImageUpload — single-image slot for the place form.
 *
 * Handles pick → validate → local preview, and keeps enough data to retry the
 * cloud upload after a failure without resetting the form (spec §8.5 P0).
 * The hook never uploads by itself and never claims a cloud save; the actual
 * upload goes through repositories.places.uploadImage from the caller.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { toAppError } from '@/lib/supabase/errors'
import type { AppError } from '@/types'
import {
  DRAFT_EMBED_MAX_BYTES,
  createPreviewUrl,
  dataUrlToFile,
  fileToDataUrl,
  prepareImage,
} from './imagePipeline'

export type ImageUploadStatus = 'idle' | 'validating' | 'ready' | 'error'

export type ImageUploadState = {
  status: ImageUploadStatus
  file: File | null
  previewUrl: string | null
  /** Small-image data-URL copy so local drafts survive a reload. */
  draftDataUrl: string | null
  width: number | null
  height: number | null
  error: AppError | null
}

const initialState: ImageUploadState = {
  status: 'idle',
  file: null,
  previewUrl: null,
  draftDataUrl: null,
  width: null,
  height: null,
  error: null,
}

export function useImageUpload() {
  const [state, setState] = useState<ImageUploadState>(initialState)
  const objectUrlRef = useRef<string | null>(null)
  const requestRef = useRef(0)

  const revokePreview = useCallback(() => {
    if (
      objectUrlRef.current &&
      typeof URL !== 'undefined' &&
      typeof URL.revokeObjectURL === 'function'
    ) {
      URL.revokeObjectURL(objectUrlRef.current)
    }
    objectUrlRef.current = null
  }, [])

  useEffect(() => revokePreview, [revokePreview])

  const selectFile = useCallback(
    async (input: File): Promise<boolean> => {
      const requestId = ++requestRef.current
      setState((previous) => ({ ...previous, status: 'validating', error: null }))
      try {
        const prepared = await prepareImage(input)
        const preview = await createPreviewUrl(prepared.file)
        let draftDataUrl: string | null = null
        if (prepared.file.size <= DRAFT_EMBED_MAX_BYTES) {
          try {
            draftDataUrl = await fileToDataUrl(prepared.file)
          } catch {
            draftDataUrl = null
          }
        }
        if (requestId !== requestRef.current) {
          if (preview.isObjectUrl && typeof URL !== 'undefined') URL.revokeObjectURL(preview.url)
          return false
        }
        revokePreview()
        if (preview.isObjectUrl) objectUrlRef.current = preview.url
        setState({
          status: 'ready',
          file: prepared.file,
          previewUrl: preview.url,
          draftDataUrl,
          width: prepared.width,
          height: prepared.height,
          error: null,
        })
        return true
      } catch (error) {
        if (requestId !== requestRef.current) return false
        setState({ ...initialState, status: 'error', error: toAppError(error, 'validation') })
        return false
      }
    },
    [revokePreview],
  )

  /** Restores the photo slot from a persisted draft data-URL after a reload. */
  const restoreFromDraftDataUrl = useCallback(
    async (dataUrl: string, fileName: string): Promise<boolean> => {
      const requestId = ++requestRef.current
      try {
        const file = await dataUrlToFile(dataUrl, fileName)
        if (requestId !== requestRef.current) return false
        revokePreview()
        setState({
          status: 'ready',
          file,
          previewUrl: dataUrl,
          draftDataUrl: dataUrl,
          width: null,
          height: null,
          error: null,
        })
        return true
      } catch {
        return false
      }
    },
    [revokePreview],
  )

  const clear = useCallback(() => {
    requestRef.current += 1
    revokePreview()
    setState(initialState)
  }, [revokePreview])

  return { ...state, selectFile, restoreFromDraftDataUrl, clear }
}
